<template>
    <div class="invoice-detail-container">
      <div class="header" v-if="invoice">
        <img class="logo" :src="apartmentLogo" alt="Apartment Logo" />
        <div class="status-badge" :class="paymentStatusClass(invoice.payment_status)">
          {{ invoice.payment_status }}
        </div>
      </div>

      <h3 v-if="invoice">Unit Bill Number: {{ invoice.invoice_number }}</h3>
<hr class="short-underline" />


      <div class="address" v-if="invoice">
        <!-- User Info section, defaults to Apartment Info if user is missing -->
        <div class="user-info">
          <p><strong>Bill To:</strong></p>
          <p>{{ invoice.user?.name || invoice.apartment?.name || 'N/A' }}</p>
          <p>{{ invoice.user?.email || invoice.apartment?.email || 'N/A' }}</p>
        </div>

        <!-- Apartment Info section, defaults to User Info if apartment is missing -->
        <div class="payment-info">
          <p><strong>From:</strong></p>
          <p>{{ invoice.apartment?.name || invoice.user?.name || 'N/A' }}</p>
          <p>{{ invoice.apartment?.address || invoice.user?.address || 'N/A' }}</p>
        </div>
      </div>


      <h4 v-if="invoice">Invoice Details</h4>
      <div class="invoice-detail" v-if="invoice">
        <p><strong>Room Number:</strong> {{ invoice.rooms ? invoice.rooms.room_number : 'N/A' }}</p>
        <p><strong>Due Date:</strong> {{ formattedDueDate }}</p>
        <p><strong>Balance: {{ selectedApartmentCurrency }}{{ invoice.balance || 0 }}</strong></p>
      </div>
      <div class="apartment-info">
        <p><strong>Payments:</strong></p>
        <p>My Payment Method{{'N/A' }}</p>
        <button> Initiate Payment</button>
      </div>

      <h3 v-if="invoice">Invoice Values</h3>
      <table class="invoice-values-table" v-if="invoice">
        <thead>
          <tr>
            <th>Description</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="description-cell">Unit Rent</td>
            <td>{{ selectedApartmentCurrency }}{{ invoice.rent_amount || 0 }}</td>
          </tr>
          <tr>
            <td class="description-cell">Water Bill</td>
            <td>{{ selectedApartmentCurrency }}{{ invoice.water_bill || 0 }}</td>
          </tr>
          <tr>
            <td class="description-cell">Electricity Bill</td>
            <td>{{ selectedApartmentCurrency }}{{ invoice.electricity_bill || 0 }}</td>
          </tr>
          <tr>
            <td><strong>Total Due</strong></td>
            <td class="total-due"><strong>{{ selectedApartmentCurrency }}{{ invoice.total_amount || 0 }}</strong></td>
          </tr>
        </tbody>
      </table>

      <!-- Transactions Section -->
      <h4>Transactions</h4>
      <div class="transactions-card" v-if="invoice && Array.isArray(payments)">
        <table class="payments-table">
          <thead>
            <tr>
              <th>Payment Date</th>
              <th>Payment Amount</th>
              <th>Payment Method</th>
              <th>Transaction ID</th>
              <th>Transaction Fee</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="payments.length === 0">
              <td colspan="5" class="no-payments">No payments done.</td>
            </tr>
            <tr
              v-else
              v-for="(payment, index) in formattedPayments"
              :key="index"
              :class="{'odd-row': index % 2 === 0, 'even-row': index % 2 !== 0}"
            >
              <td>{{ payment.payment_date }}</td>
              <td>{{ selectedApartmentCurrency }}{{ payment.payment_amount || 0 }}</td>
              <td>{{ payment.payment_method }}</td>
              <td>{{ payment.transaction_id || 'N/A' }}</td>
              <td>{{ selectedApartmentCurrency }}{{ payment.transaction_fee || 0 }}</td>
            </tr>
            <tr>
              <td colspan="4" class="balance-label"><strong>Balance</strong></td>
              <td class="balance-amount"><strong>{{ selectedApartmentCurrency }}{{ invoice.balance || 0 }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Buttons for Print and Download -->
      <div class="action-buttons">
        <button @click="printInvoice">
          <i class="fas fa-print"></i> Print
        </button>
        <button @click="downloadInvoice">
          <i class="fas fa-download"></i> Download
        </button>
      </div>
    </div>
  </template>


  <script>
  import { mapState } from 'vuex';

  export default {
    name: 'InvoiceDetail',
    data() {
      return {
        invoice: null,
        payments: [],
      };
    },
    computed: {
      ...mapState({
        apartmentInvoices: (state) => state.apartmentInvoices,
      }),
      formattedDueDate() {
        if (this.invoice && this.invoice.due_date) {
          const date = new Date(this.invoice.due_date);
          return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()} (${date.toLocaleString('default', { month: 'long' })})`;
        }
        return 'N/A';
      },
      formattedCreatedAt() {
        if (this.invoice && this.invoice.created_at) {
          const date = new Date(this.invoice.created_at);
          return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()} (${date.toLocaleString('default', { month: 'long' })})`;
        }
        return 'N/A';
      },
      formattedPayments() {
        return Array.isArray(this.payments)
          ? this.payments.map((payment) => ({
              payment_date: new Date(payment.payment_date).toLocaleDateString(),
              payment_amount: payment.payment_amount.toFixed(2),
              payment_method: payment.payment_method,
              transaction_id: payment.transaction_id || 'N/A',
              transaction_fee: payment.transaction_fee.toFixed(2),
            }))
          : [];
      },
      apartmentLogo() {
        return this.invoice?.apartment?.logo || 'default-logo.png';
      },
      selectedApartmentCurrency() {
        return 'KES '; // Update currency as needed
      },
    },
    methods: {
      paymentStatusClass(status) {
        return {
          'paid-status': status === 'paid',
          'pending-status': status === 'pending',
          'overdue-status': status === 'overdue',
        };
      },
      fetchInvoiceDetail() {
        const invoiceId = this.$route.params.id;
        const invoice = this.apartmentInvoices.find((inv) => inv.id === Number(invoiceId));
        this.invoice = invoice || null;
        if (this.invoice && Array.isArray(this.invoice.payments)) {
          this.payments = this.invoice.payments;
        }
      },
    },
    mounted() {
      this.fetchInvoiceDetail();
    },
  };
  </script>

  <style scoped>
  .invoice-detail-container {
    padding: 20px 10px;
    background-color: #f9f9f9;
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);

    max-width: 800px;
    margin: auto;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

  }

  .logo {
    max-width: 100px;
  }

  .status-badge {
    padding: 8px 15px;
    text-transform: capitalize;
    border-radius: 5px;
    color: white;
    font-weight: bold;
  }

  .paid-status {
    background-color: green;
  }

  .pending-status {
    background-color: orange;
  }

  .overdue-status {
    background-color: red;
  }

  .address {
    display: flex;
    justify-content: space-between;
    margin-bottom: 30px;
  }

  .user-info,
  .apartment-info {
    width: 45%;
  }

  .invoice-values-table {
    width: 100%;
    margin: 30px 0;
    background-color: #fff;
    border-radius: 8px;
    overflow: hidden;
  }

  .invoice-values-table th,
  .invoice-values-table td {

    border-top: 1px solid #ddd;
    text-align: left;
  }

  .invoice-values-table th {
    background-color: #f1f1f1;
    font-weight: bold;
  }

  .description-cell {
    width: 75%; /* 3/4 of the table width */
  }

  .total-due {
    font-weight: bold;
    text-align: right;
  }

  .transactions-card {
    background-color: #fff;
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }

  .payments-table {
    width: 100%;
    border-collapse: collapse;
  }

  .payments-table th,
  .payments-table td {

    border-top: 1px solid #ddd;
    text-align: left;
  }

  .payments-table th {
    background-color: #f1f1f1;
    font-weight: bold;
  }

  .odd-row {
    background-color: #f9f9f9;
  }

  .even-row {
    background-color: #fff;
  }

  .no-payments {
    text-align: center;
    font-style: italic;
    color: #666;
  }

  .balance-label {
    text-align: right;
  }
  .short-underline {
    width: 90%; /* Adjust the width as needed */
    margin: 5px auto 20px; /* Centers the underline and adds spacing */
    border: 0;
    border-bottom: 2px solid #868585; /* Adjust color and thickness as needed */
  }


  .balance-amount {
    font-weight: bold;
    text-align: right;
  }

  @media (max-width: 768px) {
    .invoice-detail-container {
      padding: 20px 10px;
    }
  }
  .action-buttons button {
    display: flex;
    background-color: #9ab6d4;
    color: #1a1111;
    border: none;
    padding: 8px 12px;
    border-radius: 5px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .action-buttons button i {
    font-size: 16px;
  }

  .action-buttons button:hover {
    background-color: #0056b3;
  }
  .invoice-detail-container {
    padding: 20px 10px; /* Reduced padding */
    background-color: #f9f9f9;
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    max-width: 800px;
    margin: auto;
  }

  .invoice-detail-container h3,
  .invoice-detail-container h4,
  .invoice-detail-container p,
  .invoice-detail-container table {
    margin-top: 10px;
    margin-bottom: 10px; /* Reduces space between elements */
  }

  /* Optional: Further refine spacing for specific elements */
  .invoice-detail-container .header,
  .invoice-detail-container .address,
  .invoice-detail-container .invoice-detail,
  .invoice-detail-container .transactions-card {
    margin-bottom: 15px; /* Less space at the bottom of sections */
  }

  /* Adjusting specific table padding */
  .invoice-values-table th,
  .invoice-values-table td,
  .payments-table th,
  .payments-table td {
    padding: 5px 10px; /* Tightens table cell padding */
  }

  </style>
